import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    MenuItem, Box, Autocomplete, Checkbox,
} from '@mui/material';
import LinkAssessmentDialog from "./LinkAssessmentDialog.comp";
import {countries} from "../../../utils/countries";
import {LANGUAGES_SUPPORTED} from "../../../utils/constants";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const defaultFields = {
    org_name: '',
    contact_name: '',
    contact_email: '',
    preferred_locale: '',
    org_address: '',
    org_city: '',
    postal_code: '',
    org_country: '',
    callback_url: 'To be Decided',
    callback_key: 'To be Decided',
};

const EditClientDialog = ({ open, onClose, onSubmit, loading, client }) => {
    const [fields, setFields] = useState(defaultFields);
    const [errors, setErrors] = useState({});
    const [apiAccess, setApiAccess] = useState(false);

    useEffect(() => {
        if (open) {
            setFields(defaultFields);
            setErrors({});
            setApiAccess(false);
        }
    }, [open]);

    useEffect(() => {
        console.log(client);
        if (client && open) {
            setFields({
                org_name: client.org_name || '',
                contact_name: client.contact_name || '',
                contact_email: client.contact_email || '',
                preferred_locale: client.preferred_locale || '',
                org_address: client.org_address || '',
                org_city: client.org_city || '',
                postal_code: client.postal_code || '',
                org_country: client.country || '',
                callback_url: client.callback_url || '',
                callback_key: client.callback_key || '',
            });
            setApiAccess(client.callback_key || client.callback_url);
        } else if (open) {
            // Reset to defaults if no client data
            setFields(defaultFields);
            setApiAccess(false);
        }
    }, [client, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields(f => ({ ...f, [name]: value }));
    };

    const handleClose = () => {
        setFields(defaultFields);
        setErrors({});
        setApiAccess(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}>
                Edit Client Info
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ textAlign: 'center', fontSize: '17px', mb: 2 }}>
                    Edit the following fields about your client info.
                </Typography>

                <Box sx={{ my: 2 }}>
                    <TextField
                        label="Organization Name"
                        name="org_name"
                        value={fields.org_name}
                        onChange={handleChange}
                        error={!!errors.org_name}
                        helperText={errors.org_name}
                        fullWidth
                        sx={{ mb: 2 }}
                        autoFocus
                    />
                    <TextField
                        label="Contact Person Name"
                        name="contact_name"
                        value={fields.contact_name}
                        onChange={handleChange}
                        error={!!errors.contact_name}
                        helperText={errors.contact_name}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Contact Person Email"
                        name="contact_email"
                        value={fields.contact_email}
                        onChange={handleChange}
                        error={!!errors.contact_email}
                        helperText={errors.contact_email}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        select
                        label="Preferred Language"
                        name="preferred_locale"
                        value={fields.preferred_locale}
                        onChange={handleChange}
                        error={!!errors.preferred_locale}
                        helperText={errors.preferred_locale || ' '}
                        fullWidth
                        // sx={{ mb: 2 }}
                    >
                        {Object.entries(LANGUAGES_SUPPORTED).map(([code, name]) => (
                            <MenuItem key={code} value={code}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Organization Address (optional)"
                        name="org_address"
                        value={fields.org_address}
                        onChange={handleChange}
                        error={!!errors.org_address}
                        helperText={errors.org_address}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Organization City (optional)"
                        name="org_city"
                        value={fields.org_city}
                        onChange={handleChange}
                        error={!!errors.org_city}
                        helperText={errors.org_city}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Organization Postal Code (optional)"
                        name="postal_code"
                        value={fields.postal_code}
                        onChange={handleChange}
                        error={!!errors.postal_code}
                        helperText={errors.postal_code}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <Autocomplete
                        options={countries}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) => option.code === value?.code}
                        value={countries.find((option) => option.code === fields.org_country) || null}
                        onChange={(event, newValue) => {
                            setFields((f) => ({
                                ...f,
                                org_country: newValue?.code || "",
                            }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Organization Country"
                                variant="outlined"
                                error={!!errors.org_country}
                                helperText={errors.org_country || " "}
                            />
                        )}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        sx={{ mb: 2, mt: 1 }}
                        control={
                            <Checkbox
                                checked={apiAccess}
                                onChange={(e) => setApiAccess(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Check here if this client needs access to the Assessment via API"
                    />
                    {apiAccess && (
                        <>
                            <TextField
                                label="Callback URL"
                                name="callback_url"
                                value={fields.callback_url}
                                onChange={handleChange}
                                error={!!errors.callback_url}
                                helperText={errors.callback_url}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Callback Key"
                                name="callback_key"
                                value={fields.callback_key}
                                onChange={handleChange}
                                error={!!errors.callback_key}
                                helperText={errors.callback_key}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Box sx={{ display: 'flex', width: '100%',alignItems:'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: '20px',
                            minWidth: '120px',
                            fontWeight: 'bold',
                            borderColor: '#0097b2',
                            color: '#0097b2',
                        }}
                    >
                        Cancel
                    </Button>
                <Button
                    onClick={() => onSubmit(fields)}
                    disabled={loading}
                    sx={{
                        borderRadius: '20px',
                        backgroundColor: '#ff6468',
                        color: '#ffffff',
                        minWidth: '120px',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        '&:hover': {
                            backgroundColor: '#ff6468',
                        },
                    }}
                >
                    Save
                </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default EditClientDialog;